/**
 * The admin's own strings.
 *
 * Kept here rather than in `@awj/i18n` on purpose. That package's catalogues are shipped whole
 * to the storefront, and the back office will end up with several hundred strings a customer
 * will never see — every one of them a few bytes on every shop page. The *formatting* helpers
 * and the locale primitives are still shared; only the words are local.
 *
 * Same `{placeholder}` convention as the storefront, so anyone moving between the two is not
 * learning a second system.
 */
export const messages = {
  en: {
    'app.title': 'AWJ admin',
    'app.subtitle': 'Back office',

    'nav.dashboard': 'Dashboard',
    'nav.catalog': 'Catalogue',
    'nav.products': 'Products',
    'nav.categories': 'Categories',
    'nav.orders': 'Orders',
    'nav.coupons': 'Discount codes',
    'nav.customers': 'Customers',
    'nav.reviews': 'Reviews',
    'nav.inquiries': 'Enquiries',
    'nav.content': 'Content',
    'nav.settings': 'Settings',
    'nav.sections': 'Sections',
    'nav.groupCatalogue': 'Catalogue',
    'nav.groupSales': 'Sales',
    'nav.groupShopfront': 'Shopfront',
    'nav.groupSystem': 'System',
    'nav.primary': 'Main navigation',
    'nav.collapse': 'Collapse navigation',
    'nav.expand': 'Expand navigation',
    'nav.viewShop': 'View shop',
    'nav.waiting': 'waiting',
    'nav.breadcrumb': 'You are here',
    'nav.account': 'Account menu',

    'auth.signIn': 'Sign in',
    'auth.signOut': 'Sign out',
    'auth.email': 'Email address',
    'auth.password': 'Password',
    'auth.showPassword': 'Show password',
    'auth.hidePassword': 'Hide password',
    'auth.signingIn': 'Signing in',
    'auth.failed': 'That email and password do not match a staff account',
    'auth.missingFields': 'Enter your email address and password',
    'auth.emailNotConfirmed': 'Confirm your email address before signing in',
    'auth.lockedOut': 'Too many attempts. This account is locked for a short while',
    'auth.blocked': 'This account has been disabled',
    'auth.notStaff': 'This account does not have access to the admin panel.',
    'auth.checking': 'Checking your session',
    'auth.lede': 'Sign in with your staff account.',
    'auth.storefront': 'Go to the shop',

    'common.retry': 'Try again',
    'common.loading': 'Loading',
    'common.somethingWentWrong': 'Something went wrong',
    'common.somethingWentWrongHint': 'The panel could not reach the server. Try again in a moment.',
    'common.notFound': 'That page does not exist',
    'common.backToDashboard': 'Back to the dashboard',
    'common.language': 'Language',
    'common.role': 'Role',

    'role.SuperAdmin': 'Super admin',
    'role.Admin': 'Admin',

    'common.viewAll': 'View all',
    'common.save': 'Save',
    'common.saving': 'Saving',
    'common.cancel': 'Cancel',
    'common.close': 'Close',
    'common.edit': 'Edit',
    'common.delete': 'Delete',
    'common.remove': 'Remove',
    'common.actions': 'Actions',
    'common.previous': 'Previous',
    'common.next': 'Next',
    'common.opensInNewTab': 'opens in a new tab',
    'common.pagination': 'Pagination',
    'common.totalRows': '{count} in total',
    'common.confirm': 'Confirm',
    'common.clearFilters': 'Clear filters',
    'common.back': 'Back',
    'common.moreInfo': 'More information',
    'common.notFoundHint': 'The address may have been mistyped, or the thing it pointed at has since been removed.',
    'common.search': 'Search',
    'common.noMatches': 'No matches',
    'common.clearDate': 'Clear the date',
    'common.chooseDate': 'Choose a date',

    'export.export': 'Export',
    'export.excel': 'Excel',
    'export.pdf': 'PDF',
    'export.working': 'Exporting',
    'export.exportedAt': 'Exported {at}',
    'export.ordersFor': 'Orders for {name}',
    'export.itemsIn': 'Items in order {number}',
    'export.tooManyRows': 'A PDF holds up to {max} rows. Narrow the filters, or export to Excel instead.',
    'export.allMatching': 'Downloads every row that matches the filters, not only this page',
    'export.failed': 'Could not export',
    'export.failedHint': 'The file was not created. Check the connection and try again.',
    'export.nameEn': 'Name (English)',
    'export.nameAr': 'Name (Arabic)',
    'export.phone': 'Phone',
    'export.stockAlert': 'Stock alert',
    'export.starts': 'Starts',
    'export.ends': 'Ends',
    'export.details': 'Details',
    'export.actionCode': 'Action code',
    'export.yes': 'Yes',
    'export.no': 'No',

    'lang.arabic': 'Arabic',
    'lang.english': 'English',

    'catalog.product': 'Product',
    'catalog.newProduct': 'New product',
    'catalog.editProduct': 'Edit product',
    'catalog.productNotFound': 'That product no longer exists.',
    'catalog.categoryNotFound': 'That category no longer exists.',
    'catalog.noProducts': 'No matching products',
    'catalog.noProductsHint': 'Try clearing a filter, or search for something else.',
    'catalog.searchProducts': 'Search by name or SKU',
    'catalog.allCategories': 'All categories',
    'catalog.allStatuses': 'All statuses',
    'catalog.sort': 'Sort',
    'catalog.image': 'Image',
    'catalog.featured': 'Featured',
    'catalog.lowStock': 'Low',
    'catalog.outOfStock': 'Out of stock',

    'catalog.basics': 'Basics',
    'catalog.name': 'Name',
    'catalog.slug': 'URL slug',
    'catalog.slugHint': 'The address this product is sold at. Changing it breaks existing links.',
    'catalog.shortDescription': 'Short description',
    'catalog.shortDescriptionHint': 'One line, shown on listing cards.',
    'catalog.description': 'Description',

    'catalog.seo': 'Search engines',
    'catalog.metaTitle': 'Meta title',
    'catalog.metaTitleHint': 'Falls back to the product name when empty.',
    'catalog.metaDescription': 'Meta description',
    'catalog.metaDescriptionHint': 'Around 155 characters. Falls back to the short description.',

    'catalog.pricing': 'Pricing',
    'catalog.price': 'Price',
    'catalog.compareAtPrice': 'Compare-at price',
    'catalog.compareAtHint': 'Shown struck through beside the price. Leave empty for no discount.',
    'catalog.vatBreakdown': 'VAT inclusive — {net} net plus {vat} VAT.',

    'catalog.stock': 'Stock',
    'catalog.stockQuantity': 'Quantity in stock',
    'catalog.stockIsPerVariant': 'The total of the variants. Each variant keeps its own stock in the Variants table.',
    'catalog.lowStockThreshold': 'Low stock at',
    'catalog.lowStockHint': 'Flagged on the dashboard once stock drops to this.',

    'catalog.placement': 'Placement',
    'catalog.category': 'Category',
    'catalog.chooseCategory': 'Choose a category',
    'catalog.status': 'Status',
    'catalog.sku': 'SKU',
    'catalog.featureOnHome': 'Feature on the home page',

    'catalog.options': 'Options',
    'catalog.addOption': 'Add option',
    'catalog.noOptionsHint': 'Add an option — size, grind — if this product is sold in more than one form.',
    'catalog.optionName': 'Option name',
    'catalog.optionNameAr': 'الحجم',
    'catalog.optionNameEn': 'Size',
    'catalog.optionValue': 'Value',
    'catalog.addValue': 'Add value',
    'catalog.valueAr': '٢٥٠ جم',
    'catalog.valueEn': '250 g',

    'catalog.variants': 'Variants',
    'catalog.variant': 'Variant',
    'catalog.generateVariants': 'Build variant rows',
    'catalog.optionsIncomplete': 'Give every option at least one value before building the rows.',
    'catalog.variantsOutOfDate':
      'The options have changed: {expected} combinations, {actual} rows. Rebuild to match.',
    'catalog.priceDelta': 'Price change',
    'catalog.finalPrice': 'Sells for',
    'catalog.active': 'Active',
    'catalog.hidden': 'Hidden',

    'catalog.images': 'Images',
    'catalog.uploadImages': 'Upload images',
    'catalog.noImages': 'No images yet. The first one is the picture the shop lists.',
    'catalog.primaryImage': 'Main',
    'catalog.moveEarlier': 'Move earlier',
    'catalog.moveLater': 'Move later',
    'catalog.alt': 'Alt',
    'catalog.editAlt': 'Edit alt text',
    'catalog.altAr': 'وصف الصورة',
    'catalog.altEn': 'What the photo shows',
    'catalog.uploadFailed': 'Could not upload {name}',

    'catalog.saved': 'Saved',
    'catalog.slugAdjusted': 'The web address was saved as “{slug}”.',
    'catalog.couldNotSave': 'Could not save',
    'catalog.deleted': 'Deleted',
    'catalog.couldNotDelete': 'Could not delete',
    'catalog.hasOrders': 'This product has been ordered, so it stays on record. Archive it instead.',
    'catalog.archive': 'Archive',
    'catalog.archived': 'Archived',
    'catalog.restore': 'Restore',
    'catalog.restored': 'Restored',
    'catalog.couldNotArchive': 'Could not archive',
    'catalog.couldNotRestore': 'Could not restore',
    'catalog.deleteProduct': 'Delete product',
    'catalog.deleteHint': 'Permanent. Archive instead if it has ever been ordered.',
    'catalog.deleteTitle': 'Delete “{name}”?',
    'catalog.deleteExplain':
      'This removes the product, its variants and its images for good. A product that has been ordered cannot be deleted — archive it instead.',
    'catalog.required': 'Required',
    'catalog.bothLanguagesRequired': 'Both languages are required',
    'catalog.pricePositive': 'Enter a price above zero',

    'catalog.newCategory': 'New category',
    'catalog.editCategory': 'Edit category',
    'catalog.categoryDialogLede': 'Both languages are needed before it can be saved.',
    'catalog.noCategories': 'No categories yet.',
    'catalog.productCount': 'Products',
    'catalog.parentCategory': 'Sits under',
    'catalog.topLevel': 'Top level',
    'catalog.sortOrder': 'Order',
    'catalog.showInShop': 'Show in the shop',

    'productStatus.Draft': 'Draft',
    'productStatus.Active': 'Active',
    'productStatus.Archived': 'Archived',

    'catalogSort.newest': 'Newest first',
    'catalogSort.name': 'Name',
    'catalogSort.price-asc': 'Price, low to high',
    'catalogSort.price-desc': 'Price, high to low',
    'catalogSort.stock': 'Stock, low to high',

    'dashboard.title': 'Dashboard',
    'dashboard.subtitle': 'Trade and this month’s performance',
    'dashboard.trade': 'Trade',
    'dashboard.needsAttention': 'Needs attention',
    'dashboard.revenueThisMonth': 'Revenue this month',
    'dashboard.ordersThisMonth': 'Orders this month',
    'dashboard.revenueToday': 'Revenue today',
    'dashboard.products': 'Products',
    'dashboard.customers': 'Customers',
    'dashboard.pendingPayment': 'Awaiting payment',
    'dashboard.awaitingFulfilment': 'To prepare',
    'dashboard.lowStock': 'Low stock',
    'dashboard.pendingReviews': 'Reviews to moderate',
    'dashboard.newInquiries': 'New enquiries',
    'dashboard.shippedToday': 'Shipped today',
    'dashboard.refundsDue': 'Refunds due',
    'dashboard.recentOrders': 'Recent orders',
    'dashboard.noOrdersYet': 'No orders yet.',

    'orders.number': 'Order',
    'orders.customer': 'Customer',
    'orders.placed': 'Placed',
    'orders.status': 'Status',
    'orders.total': 'Total',
    'orders.order': 'Order',
    'orders.search': 'Search by number, email or name',
    'orders.allStatuses': 'All statuses',
    'orders.from': 'From',
    'orders.to': 'To',
    'orders.none': 'No matching orders',
    'orders.noneHint': 'Try clearing a filter, or widening the date range.',
    'orders.items': 'Items',
    'orders.item': 'Item',
    'orders.unitPrice': 'Unit price',
    'orders.quantity': 'Qty',
    'orders.lineTotal': 'Line total',
    'orders.subtotal': 'Subtotal',
    'orders.discount': 'Discount',
    'orders.shipping': 'Shipping',
    'orders.freeShipping': 'Free',
    'orders.vatIncluded': 'Of which VAT',
    'orders.timeline': 'History',
    'orders.bySystem': 'The shop',
    'orders.shipTo': 'Deliver to',
    'orders.payment': 'Payment',
    'orders.paid': 'Paid',
    'orders.notPaid': 'Not paid yet',
    'orders.noReference': 'No payment reference yet.',
    'orders.notFound': 'That order does not exist.',
    'orders.placedAt': 'Placed {at}',
    'orders.paidAt': 'Paid {at}',
    'orders.cancelledAt': 'Cancelled {at}',
    'orders.finished': 'Nothing further to do.',
    'orders.courier': 'Courier',
    'orders.trackingNumber': 'Tracking number',
    'orderNote.placed': 'Order placed.',
    'orderNote.paid': 'Payment received.',
    'orderNote.declined': 'Payment declined.',
    'orderNote.expired': 'Cancelled: not paid in time.',
    'orderNote.cancelledByCustomer': 'Cancelled by the customer.',
    'orderNote.cancelledByCustomerBecause': 'Cancelled by the customer: {reason}',
    'coupons.new': 'New code',
    'coupons.edit': 'Edit code',
    'coupons.editorLede': 'What it takes off, what it asks of the basket, and when it can be used.',
    'coupons.none': 'No discount codes yet.',
    'coupons.notFound': 'No such discount code.',
    'coupons.code': 'Code',
    'coupons.codeHint': 'What shoppers type. Letters, numbers, dashes and underscores; not case-sensitive.',
    'coupons.discount': 'Discount',
    'coupons.type': 'Kind',
    'coupons.typePercentage': 'Percentage off',
    'coupons.typeFixed': 'Fixed amount off',
    'coupons.percentOff': 'Percent off',
    'coupons.amountOff': 'Amount off (AED)',
    'coupons.minimum': 'Minimum basket (AED)',
    'coupons.minimumHint': 'Optional. The code only applies once the basket reaches this.',
    'coupons.cap': 'Most it can take off (AED)',
    'coupons.capHint': 'Optional. Stops a percentage taking too much off a large basket.',
    'coupons.limits': 'When and how often',
    'coupons.usageLimit': 'Total uses',
    'coupons.usageLimitHint': 'Optional. Across all shoppers. Leave empty for no limit.',
    'coupons.switchedOn': 'Switched on',
    'coupons.switchedOnHint': 'Switch off to stop the code working without deleting it.',
    'coupons.window': 'Valid',
    'coupons.used': 'Used',
    'coupons.usedOf': '{used} of {limit}',
    'coupons.minimumShort': 'Basket over {amount}',
    'coupons.capShort': 'Up to {amount}',
    'coupons.fromDate': 'From {date}',
    'coupons.untilDate': 'Until {date}',
    'coupons.noEnd': 'No end date',
    'coupons.live': 'Live',
    'coupons.off': 'Off',
    'coupons.expired': 'Expired',
    'coupons.usedUp': 'Used up',
    'coupons.scheduled': 'Scheduled',
    'coupons.saved': 'Discount code saved',
    'coupons.deleted': 'Discount code deleted',
    'coupons.delete': 'Delete code',
    'coupons.deleteTitle': 'Delete “{code}”?',
    'coupons.deleteHint': 'Only a code nobody has used can be deleted. Once used, switch it off instead.',
    'coupons.inUse': 'This code has been used on orders, so it stays on record. Switch it off instead.',
    'coupons.codeInvalid': 'Enter a code using letters, numbers, dashes or underscores.',
    'coupons.codeTaken': 'Another discount code already uses this code.',
    'coupons.valueInvalid': 'Enter a discount greater than zero.',
    'coupons.percentTooHigh': 'A percentage cannot be more than 100.',
    'coupons.endsBeforeStart': 'The code must end after it starts.',
    'orders.refundDue': 'Refund due',
    'orders.refundDueAmount': 'Refund due: {amount}',
    'orders.refundHow': 'Return the payment at the bank or in the payment gateway, then record it here.',
    'orders.recordRefund': 'Record refund',
    'orders.recordRefundTitle': 'Record the refund of {amount}',
    'orders.refundReference': 'Bank or gateway reference',
    'orders.refundReferenceHint': 'Optional. Quoted when the accounts are reconciled.',
    'orders.markRefunded': 'Mark refunded',
    'orders.refundRecorded': 'Refund recorded',
    'orders.couldNotRecordRefund': 'Could not record the refund',
    'orders.refundedAt': 'Refunded {at}',
    'orders.cancelPaidHint':
      'This order has been paid. Cancelling it marks a refund of {amount} as due. Return the money at the bank or in the payment gateway, then record it on this page.',
    'orders.nextStepsFailed': 'Could not load what can happen next.',
    'orders.note': 'Note for the customer',
    'orders.noteHint': 'Optional. Shown on the customer’s order page and included in the email they receive.',
    'orders.noteHintNoEmail': 'Optional. Shown on the customer’s order page. This step sends no email.',
    'orders.noteHintNoAddress': 'Optional. Shown on the order’s tracking page. This order has no email address to send to.',
    'orders.confirmMove': 'Move to “{status}”?',
    'orders.moved': 'Moved to “{status}”',
    'orders.couldNotMove': 'Could not move this order',

    'orderAction.Paid': 'Mark paid',
    'orderAction.Processing': 'Start preparing',
    'orderAction.Shipped': 'Mark shipped',
    'orderAction.Delivered': 'Mark delivered',
    'orderAction.Cancelled': 'Cancel order',
    'orderAction.PaymentFailed': 'Mark payment failed',

    'customers.customer': 'Customer',
    'customers.search': 'Search by name, email or phone',
    'customers.account': 'Account',
    'customers.allAccounts': 'All accounts',
    'customers.activeOnly': 'Active only',
    'customers.blockedOnly': 'Blocked only',
    'customers.sort': 'Sort',
    'customers.hasOrdered': 'Has ordered',
    'customers.none': 'No matching customers',
    'customers.noneHint': 'Try clearing a filter, or search by email instead.',
    'customers.registered': 'Registered',
    'customers.lastOrder': 'Last order',
    'customers.orders': 'Orders',
    'customers.latestOrders': 'The latest {shown} of {total}.',
    'customers.allOrders': 'See all their orders',
    'customers.spent': 'Spent',
    'customers.spend': 'Spend',
    'customers.neverOrdered': 'Has not ordered.',
    'customers.blocked': 'Blocked',
    'customers.active': 'Active',
    'customers.unconfirmed': 'Email not confirmed',
    'customers.notFound': 'That customer does not exist.',
    'customers.registeredAt': 'Registered {at}',
    'customers.blockedAt': 'Blocked {at}',
    'customers.contact': 'Contact',
    'customers.writesIn': 'Written to in {language}.',
    'customers.addresses': 'Addresses',
    'customers.noAddresses': 'No saved addresses.',
    'customers.defaultAddress': 'Default',
    'customers.reviews': 'Reviews',
    'customers.noReviews': 'Has not reviewed anything.',
    'customers.rating': '{rating} out of 5',
    'customers.block': 'Block',
    'customers.unblock': 'Unblock',
    'customers.resendConfirmation': 'Send confirmation email',
    'customers.confirmationSent': 'Confirmation email sent to {email}',
    'customers.couldNotSendConfirmation': 'Could not send the confirmation email',
    'customers.blockTitle': 'Block this account?',
    'customers.blockExplain':
      'They will not be able to sign in. Their orders, reviews and addresses stay exactly as they are, and you can undo this at any time.',
    'customers.reason': 'Reason',
    'customers.reasonHint': 'Internal. Kept in the audit log and never shown to the customer.',
    'customers.blockedNow': 'Account blocked',
    'customers.unblocked': 'Account unblocked',
    'customers.couldNotChange': 'Could not change this account',
    'customers.deleteTitle': 'Delete this account?',
    'customers.deleteExplain':
      'Permanent. Only possible because this customer has never ordered — block the account instead if you only want to stop them signing in.',
    'customers.deleted': 'Account deleted',
    'customers.couldNotDelete': 'Could not delete this account',

    'customerSort.newest': 'Newest first',
    'customerSort.name': 'Name',
    'customerSort.spent': 'Spent, most first',
    'customerSort.orders': 'Orders, most first',

    'reviewStatus.Pending': 'Waiting',
    'reviewStatus.Approved': 'Published',
    'reviewStatus.Hidden': 'Rejected',

    'reviews.all': 'All',
    'reviews.filterByStatus': 'Filter by status',
    'reviews.none': 'Nothing here',
    'reviews.inboxClear': 'The queue is clear',
    'reviews.inboxClearHint': 'New reviews land here for moderation before they reach the shop.',
    'reviews.verified': 'Verified purchase',
    'reviews.publish': 'Publish',
    'reviews.reject': 'Reject',
    'reviews.published': 'Review published',
    'reviews.rejected': 'Review rejected',
    'reviews.couldNotModerate': 'Could not moderate this review',
    'reviews.reply': 'Reply',
    'reviews.editReply': 'Edit reply',
    'reviews.replyTitle': 'Reply to this review',
    'reviews.replyHint': 'Published under the review on the shop, signed as AWJ.',
    'reviews.replied': 'Reply saved',
    'reviews.couldNotReply': 'Could not save the reply',
    'reviews.shopReplied': 'AWJ replied',

    'inquiries.search': 'Search by name, email, company or subject',
    'inquiries.status': 'Status',
    'inquiries.type': 'Type',
    'inquiries.allStatuses': 'All statuses',
    'inquiries.allTypes': 'All types',
    'inquiries.none': 'No enquiries match these filters.',
    'inquiries.pickOne': 'Nothing open',
    'inquiries.pickOneHint': 'Choose an enquiry from the list to read it and reply.',
    'inquiries.notFound': 'That enquiry no longer exists.',
    'inquiries.noteCount': 'Notes: {count}',
    'inquiries.pickedUpBy': 'picked up by {who}',
    'inquiries.company': 'Company',
    'inquiries.quantity': 'Estimated quantity',
    'inquiries.frequency': 'How often',
    'inquiries.notes': 'Internal notes',
    'inquiries.noNotes': 'No notes yet.',
    'inquiries.addNote': 'Add a note',
    'inquiries.someone': 'A colleague',
    'inquiries.emailBack': 'Email back',
    'inquiries.movedTo': 'Moved to “{status}”',
    'inquiries.couldNotMove': 'Could not move this enquiry',
    'inquiries.couldNotAddNote': 'Could not add the note',

    'inquiryStatus.New': 'New',
    'inquiryStatus.InProgress': 'In progress',
    'inquiryStatus.Resolved': 'Resolved',
    'inquiryStatus.Spam': 'Spam',

    'inquiryAction.New': 'Reopen',
    'inquiryAction.InProgress': 'Pick up',
    'inquiryAction.Resolved': 'Resolve',
    'inquiryAction.Spam': 'Mark spam',

    'inquiryType.Contact': 'Contact',
    'inquiryType.Business': 'Trade enquiry',

    'content.sections': 'Content areas',
    'content.announcements': 'Announcements',
    'content.faq': 'FAQ',
    'content.media': 'Images',
    'content.saved': 'Saved',
    'content.couldNotSave': 'Could not save',
    'content.deleted': 'Deleted',
    'content.couldNotDelete': 'Could not delete',
    'content.live': 'On the site',
    'content.hidden': 'Hidden',
    'content.showOnSite': 'Show on the site',


    'content.announcementsLede': 'The strip across the top of the shop.',
    'content.announcementNotFound': 'That announcement no longer exists.',
    'content.newAnnouncement': 'New announcement',
    'content.editAnnouncement': 'Edit announcement',
    'content.noAnnouncements': 'No announcements yet',
    'content.noAnnouncementsHint': 'An announcement is the strip across the top of the shop. Add one and give it a window of dates.',
    'content.message': 'Message',
    'content.linkUrl': 'Link',
    'content.linkLabel': 'Link label',
    'content.schedule': 'When it shows',
    'content.startsAt': 'Starts',
    'content.endsAt': 'Ends',
    'content.startsAtHint': 'Leave empty to start straight away.',
    'content.timeOfDay': 'Time of day',
    'content.switchedOn': 'Switched on',
    'content.showingNow': 'Showing now',
    'content.scheduled': 'Scheduled',
    'content.switchedOff': 'Switched off',
    'content.from': 'from {at}',
    'content.until': 'until {at}',

    'content.faqLede': 'The questions the shop answers, in both languages.',
    'content.newQuestion': 'New question',
    'content.noFaq': 'No questions yet',
    'content.noFaqHint': 'Questions added here appear on the shop, in the order you set.',
    'content.question': 'Question',
    'content.answer': 'Answer',
    'content.answerRequired': 'An answer is required in both languages',
    'content.category': 'Group',
    'content.categoryHint': 'Optional. Questions with the same group are shown together.',

    'content.mediaLede': 'Pictures used across the shop. Pages pick them from here.',
    'content.uploadMedia': 'Upload',
    'content.noMedia': 'Nothing uploaded yet',
    'content.noMediaHint': 'Upload a picture here, or from the editor of the page that needs it.',
    'content.noAlt': 'No alt text — a screen reader will announce nothing.',


    'content.formatting': 'Formatting',
    'content.bold': 'Bold',
    'content.italic': 'Italic',
    'content.bulletList': 'Bulleted list',
    'content.numberedList': 'Numbered list',
    'content.link': 'Link',
    'content.linkPrompt': 'Where should this link go?',
    'content.pages': 'Pages',
    'content.groupShop': 'Shop pages',
    'content.groupPolicies': 'Policies',
    'content.groupOther': 'Other pages',
    'content.pageHome': 'Home page',
    'content.pageHomeSummary': 'The banner at the top and the heading over the featured products.',
    'content.pageStory': 'Our story',
    'content.pageStorySummary': 'The story page: its heading, its picture and the story itself.',
    'content.pageMenu': 'Menu',
    'content.pageMenuSummary': 'The words above the café menu.',
    'content.pageBusiness': 'For business',
    'content.pageBusinessSummary': 'The trade page: its introduction and any extra sections.',
    'content.pageContact': 'Contact',
    'content.pageContactSummary': 'The heading and the line at the top of the contact page.',
    'content.pagePrivacy': 'Privacy policy',
    'content.pageTerms': 'Terms and conditions',
    'content.pagePolicySummary': 'The heading, a short summary, and the numbered sections.',
    'content.pageOtherSummary': 'Copy saved under this page name. The shop may not show it anywhere.',
    'content.pageNotFound': 'That page is not one the studio knows.',
    'content.partHero': 'Banner',
    'content.partHeroHint': 'The first thing a visitor sees on the home page.',
    'content.partFeatured': 'Featured products',
    'content.partFeaturedHint': 'The heading over the products picked for the home page.',
    'content.partStory': 'The story',
    'content.partStoryHint': 'Shown on the story page, with its picture beside it.',
    'content.partHeading': 'Page heading',
    'content.partMenuHint': 'Above the menu. Leave it empty to use the shop’s own wording.',
    'content.partBusinessHint': 'The top of the trade page, above the enquiry form.',
    'content.partContactHint': 'The top of the contact page.',
    'content.partPolicyHint': 'The title, a line under it, and the summary readers see first.',
    'content.fieldTitle': 'Heading',
    'content.fieldSubtitle': 'Line under the heading',
    'content.fieldBody': 'Text',
    'content.fieldSummary': 'Summary at a glance',
    'content.fieldButton': 'Button text',
    'content.fieldButtonLink': 'Button link',
    'content.fieldButtonLinkHint': 'A page of the shop, like /shop, or a full web address. The same in both languages.',
    'content.fieldImage': 'Picture',
    'content.showOnSiteHint': 'Switched off, it stays here but the shop does not show it.',
    'content.writingIn': 'Writing in',
    'content.gaps': '{count} missing',
    'content.otherSays': 'Not written yet. In {language}:',
    'content.viewOnSite': 'View on the shop',
    'content.sectionsTitle': 'Sections',
    'content.sectionsHint': 'Shown in this order, numbered. Nothing changes on the shop until you save the page.',
    'content.addSection': 'Add a section',
    'content.untitledSection': 'Untitled section',
    'content.moveUp': 'Move “{name}” up',
    'content.moveDown': 'Move “{name}” down',
    'content.moveUpShort': 'Move up',
    'content.moveDownShort': 'Move down',
    'content.removeSection': 'Remove “{name}”',
    'content.savePage': 'Save page',
    'content.unsaved': 'Unsaved changes',
    'content.leaveTitle': 'Leave without saving?',
    'content.leaveHint': 'The changes you made to this page will be lost.',
    'editor.leaveHint': 'The changes you have not saved will be lost.',
    'audit.auth.login.succeeded': 'Signed in',
    'audit.auth.login.failed': 'Sign-in failed',
    'audit.auth.login.blocked': 'Blocked account tried to sign in',
    'audit.auth.lockout': 'Locked out after failed attempts',
    'audit.auth.registered': 'Account created',
    'audit.auth.email.confirmed': 'Email confirmed',
    'audit.auth.email.confirmation_resent': 'Confirmation email sent again',
    'audit.auth.signed_out': 'Signed out',
    'audit.auth.token.refreshed': 'Session refreshed',
    'audit.auth.token.reuse_detected': 'Stolen session suspected',
    'audit.auth.password.reset_requested': 'Password reset requested',
    'audit.auth.password.reset': 'Password reset',
    'audit.auth.password.changed': 'Password changed',
    'audit.auth.profile.updated': 'Profile updated',
    'audit.auth.external.linked': 'Google account linked',
    'audit.customer.blocked': 'Customer blocked',
    'audit.customer.unblocked': 'Customer unblocked',
    'audit.customer.deleted': 'Customer deleted',
    'audit.staff.invited': 'Staff invited',
    'audit.staff.role_changed': 'Staff role changed',
    'audit.staff.blocked': 'Staff blocked',
    'audit.staff.unblocked': 'Staff unblocked',
    'audit.catalog.category.created': 'Category created',
    'audit.catalog.category.updated': 'Category updated',
    'audit.catalog.category.deleted': 'Category deleted',
    'audit.catalog.product.created': 'Product created',
    'audit.catalog.product.updated': 'Product updated',
    'audit.catalog.product.status_changed': 'Product status changed',
    'audit.catalog.product.image_added': 'Product picture added',
    'audit.catalog.product.deleted': 'Product deleted',
    'audit.content.block.created': 'Content added',
    'audit.content.block.updated': 'Content updated',
    'audit.content.media.deleted': 'Media deleted',
    'audit.coupon.created': 'Discount code created',
    'audit.coupon.updated': 'Discount code updated',
    'audit.coupon.deleted': 'Discount code deleted',
    'audit.inquiry.received': 'Enquiry received',
    'audit.inquiry.status_changed': 'Enquiry status changed',
    'audit.order.placed': 'Order placed',
    'audit.order.paid': 'Order paid',
    'audit.order.cancelled': 'Order cancelled',
    'audit.order.status_changed': 'Order status changed',
    'audit.order.refunded': 'Refund recorded',
    'audit.review.submitted': 'Review submitted',
    'audit.review.moderated': 'Review moderated',
    'audit.review.replied': 'Review replied to',
    'audit.settings.updated': 'Settings changed',
    'confirm.deleteNamed': 'Delete “{name}”?',
    'confirm.deleteQuestion': 'Delete “{name}”?',
    'confirm.deleteAnnouncement': 'Delete this announcement?',
    'confirm.deleteImage': 'Delete this picture?',
    'confirm.deleteImageBody': 'It is taken off the product and its file is deleted. This cannot be undone.',
    'confirm.deleteMediaBody': 'Anywhere on the shop that uses it will show nothing in its place. This cannot be undone.',
    'confirm.cannotUndo': 'This cannot be undone.',
    'content.keepEditing': 'Keep editing',
    'content.discard': 'Discard changes',
    'content.sectionCount': 'Sections: {count}',
    'content.hiddenCount': '{count} hidden',
    'content.missingArabic': 'Arabic missing in {count}',
    'content.missingEnglish': 'English missing in {count}',
    'content.bothLanguages': 'Both languages complete',
    'content.featuredNow': '{count} products featured now',
    'content.chooseFeatured': 'Choose them in Products',
    'content.chooseImage': 'Choose a picture',
    'content.chooseImageHint': 'From the image library, or upload a new one.',
    'content.changeImage': 'Change picture',
    'content.libraryLede': 'Pick a picture, or upload one from your computer.',
    'content.uploadNew': 'Upload new',
    'content.useImage': 'Use this picture',
    'content.bodyPlaceholder': 'Start writing…',
    'content.textStyle': 'Text style',
    'content.styleParagraph': 'Paragraph',
    'content.styleHeading': 'Heading',
    'content.styleSubheading': 'Subheading',
    'content.underline': 'Underline',
    'content.quote': 'Quote',
    'content.undo': 'Undo',
    'content.redo': 'Redo',
    'content.linkApply': 'Apply',
    'content.linkRemove': 'Remove link',
    'content.editQuestion': 'Edit question',
    'content.questionNotFound': 'That question no longer exists.',
    'content.scheduleHint': 'Leave both empty to show it until it is switched off.',
    'content.switchedOnHint': 'Switched off, it never shows, whatever the dates.',

    'settings.shop': 'Shop',
    'settings.staff': 'Staff',
    'settings.audit': 'Audit log',
    'settings.saved': 'Saved',
    'settings.couldNotSave': 'Could not save',
    'settings.noChanges': 'No changes',
    'settings.superAdminOnly': 'Only a super admin can open settings.',
    'settings.secretSet': 'Set. Leave alone to keep it, or type a new one.',
    'settings.secretUnset': 'Not set.',

    'settingGroup.Store': 'Shop details',
    'settingGroup.Localisation': 'Language and currency',
    'settingGroup.Shipping': 'Delivery',
    'settingGroup.Checkout': 'Checkout',
    'settingGroup.Email': 'Notifications',
    'settingGroup.Reviews': 'Reviews',
    'settingGroup.Maintenance': 'Maintenance',

    'settingKey.Store.NameEn': 'Shop name (English)',
    'settingKey.Store.NameAr': 'Shop name (Arabic)',
    'settingKey.Store.SupportEmail': 'Support email',
    'settingKey.Store.SupportPhone': 'Mobile number',
    'settingKey.Store.Landline': 'Landline',
    'settingKey.Store.AddressEn': 'Address (English)',
    'settingKey.Store.AddressAr': 'Address (Arabic)',
    'settingKey.Store.StorefrontBaseUrl': 'Storefront address',
    'settingKey.Store.AdminBaseUrl': 'Back office address',

    'settingKey.Localisation.DefaultLocale': 'Default language',
    'settingKey.Localisation.Currency': 'Currency',
    'settingKey.Localisation.VatRate': 'VAT rate',

    'settingKey.Shipping.ShippingFee': 'Delivery fee',
    'settingKey.Shipping.FreeShippingThreshold': 'Free delivery from',
    'settingKey.Shipping.ServedEmirates': 'Emirates served',

    'settingKey.Checkout.GuestCheckoutEnabled': 'Allow checkout without an account',
    'settingKey.Checkout.MinimumOrderAmount': 'Minimum order',
    'settingKey.Checkout.OrderNumberPrefix': 'Order number prefix',
    'settingKey.Checkout.AutoCancelUnpaidAfterHours': 'Cancel unpaid orders after (hours)',

    'settingKey.Email.AdminRecipients': 'Who receives staff notifications',
    'settings.recipientsFallback': 'Empty, so staff notifications go to the support email in Shop details.',
    'settings.checkMarked': 'Nothing was saved. Check the marked field.',
    'settings.invalidValue': 'Not valid here. Expected: {expected}',
    'settingKey.Email.NotifyOrderConfirmation': 'Tell the customer their order was received',
    'settingKey.Email.NotifyOrderShipped': 'Tell the customer their order has shipped',
    'settingKey.Email.NotifyAdminNewOrder': 'Tell staff about a new order',
    'settingKey.Email.NotifyAdminNewInquiry': 'Tell staff about a new enquiry',
    'settingKey.Email.NotifyAdminLowStock': 'Tell staff when stock runs low',

    'settingKey.Reviews.AutoApprove': 'Publish reviews without reading them first',
    'settingKey.Reviews.MinimumCharacters': 'Shortest review allowed (characters)',
    'settingKey.Reviews.RequireVerifiedPurchase': 'Only customers who bought it may review',

    'settingKey.Maintenance.Enabled': 'Take the shop offline',
    'settingKey.Maintenance.MessageEn': 'Offline message (English)',
    'settingKey.Maintenance.MessageAr': 'Offline message (Arabic)',

    'settingKind.Text': ' ',
    'settingKind.MultilineText': ' ',
    'settingKind.Number': 'A whole number.',
    'settingKind.Money': 'An amount in AED, e.g. 25.00',
    'settingKind.Percentage': 'A rate between 0 and 1, e.g. 0.05 for 5%.',
    'settingKind.Boolean': ' ',
    'settingKind.Email': 'An email address.',
    'settingKind.Url': 'A full http or https address.',
    'settingKind.Locale': '“ar” or “en”.',
    'settingKind.EmirateList': 'Emirates, comma separated. Empty means all of them.',
    'settingKind.EmailList': 'Email addresses, comma separated.',

    'settings.staffLede': 'Who can get into the back office.',
    'settings.invite': 'Invite',
    'settings.inviteTitle': 'Invite a colleague',
    'settings.inviteExplain':
      'They get an email with a link to set their own password. No password is chosen for them.',
    'settings.inviteIncomplete': 'A name and an email address are needed',
    'settings.invited': 'Invitation sent',
    'settings.couldNotInvite': 'Could not send the invitation',
    'settings.confirm.promoteTitle': 'Make {name} a super admin?',
    'settings.confirm.promoteBody':
      'Super admins can change the shop settings, manage staff, and permanently delete products and customers.',
    'settings.confirm.demoteTitle': 'Make {name} an admin?',
    'settings.confirm.demoteBody':
      'They keep the day-to-day work but lose the shop settings, staff management and permanent deletion.',
    'settings.confirm.blockTitle': 'Block {name}?',
    'settings.confirm.blockBody':
      'They are signed out and cannot sign in to the back office until someone unblocks them.',
    'settings.inviteExists':
      'That email already has an account — perhaps as a customer. Staff need an address of their own; use a work address.',
    'settings.person': 'Person',
    'settings.role': 'Role',
    'settings.roleHint': 'A super admin can also open settings, manage staff and delete records.',
    'settings.added': 'Added',
    'settings.lastSignIn': 'Last signed in',
    'settings.neverSignedIn': 'Never',
    'settings.thisIsYou': 'This is you',
    'settings.promote': 'Make super admin',
    'settings.demote': 'Make admin',
    'settings.roleChanged': 'Role changed',
    'settings.couldNotChangeRole': 'Could not change the role',
    'settings.firstName': 'First name',
    'settings.lastName': 'Last name',

    'settings.auditActor': 'Search by who did it',
    'settings.auditArea': 'Area',
    'settings.auditAllAreas': 'Everything',
    'settings.auditNone': 'Nothing recorded for these filters.',
    'settings.auditWhen': 'When',
    'settings.auditAction': 'What happened',
    'settings.auditWho': 'Who',
    'settings.auditWhat': 'On',
    'settings.auditFrom': 'From',

    'auditArea.auth': 'Sign-ins',
    'auditArea.customer': 'Customers',
    'auditArea.staff': 'Staff',
    'auditArea.order': 'Orders',
    'auditArea.catalog': 'Catalogue',
    'auditArea.review': 'Reviews',
    'auditArea.inquiry': 'Enquiries',
    'auditArea.content': 'Content',
    'auditArea.setting': 'Settings',

    'orderStatus.PendingPayment': 'Awaiting payment',
    'orderStatus.Paid': 'Paid',
    'orderStatus.PaymentFailed': 'Payment failed',
    'orderStatus.Processing': 'Being prepared',
    'orderStatus.Shipped': 'On its way',
    'orderStatus.Delivered': 'Delivered',
    'orderStatus.Cancelled': 'Cancelled',


    'a11y.notifications': 'Notifications',
    'a11y.scrollableTable': 'Scrollable table',
    'a11y.dismissNotification': 'Dismiss notification',
    'a11y.skipToContent': 'Skip to content',
  },

  ar: {
    'app.title': 'إدارة أوج',
    'app.subtitle': 'لوحة التحكم',

    'nav.dashboard': 'لوحة المعلومات',
    'nav.catalog': 'الكتالوج',
    'nav.products': 'المنتجات',
    'nav.categories': 'الفئات',
    'nav.orders': 'الطلبات',
    'nav.coupons': 'رموز الخصم',
    'nav.customers': 'العملاء',
    'nav.reviews': 'التقييمات',
    'nav.inquiries': 'الاستفسارات',
    'nav.content': 'المحتوى',
    'nav.settings': 'الإعدادات',
    'nav.sections': 'الأقسام',
    'nav.groupCatalogue': 'الكتالوج',
    'nav.groupSales': 'المبيعات',
    'nav.groupShopfront': 'واجهة المتجر',
    'nav.groupSystem': 'النظام',
    'nav.primary': 'التنقل الرئيسي',
    'nav.collapse': 'طي القائمة',
    'nav.expand': 'توسيع القائمة',
    'nav.viewShop': 'عرض المتجر',
    'nav.waiting': 'بانتظار إجراء',
    'nav.breadcrumb': 'موقعك في اللوحة',
    'nav.account': 'قائمة الحساب',

    'auth.signIn': 'تسجيل الدخول',
    'auth.signOut': 'تسجيل الخروج',
    'auth.email': 'البريد الإلكتروني',
    'auth.password': 'كلمة المرور',
    'auth.showPassword': 'إظهار كلمة المرور',
    'auth.hidePassword': 'إخفاء كلمة المرور',
    'auth.signingIn': 'جاري تسجيل الدخول',
    'auth.failed': 'البريد الإلكتروني وكلمة المرور لا يطابقان حساب موظف',
    'auth.missingFields': 'أدخل بريدك الإلكتروني وكلمة المرور',
    'auth.emailNotConfirmed': 'أكد بريدك الإلكتروني قبل تسجيل الدخول',
    'auth.lockedOut': 'محاولات كثيرة. تم قفل هذا الحساب لفترة قصيرة',
    'auth.blocked': 'تم تعطيل هذا الحساب',
    'auth.notStaff': 'هذا الحساب لا يملك صلاحية الدخول إلى لوحة التحكم.',
    'auth.checking': 'جاري التحقق من جلستك',
    'auth.lede': 'سجل الدخول بحساب الموظف الخاص بك.',
    'auth.storefront': 'الانتقال إلى المتجر',

    'common.retry': 'إعادة المحاولة',
    'common.loading': 'جاري التحميل',
    'common.somethingWentWrong': 'حدث خطأ ما',
    'common.somethingWentWrongHint': 'تعذر الوصول إلى الخادم. حاول مرة أخرى بعد قليل.',
    'common.notFound': 'هذه الصفحة غير موجودة',
    'common.backToDashboard': 'العودة إلى لوحة المعلومات',
    'common.language': 'اللغة',
    'common.role': 'الدور',

    'role.SuperAdmin': 'مدير عام',
    'role.Admin': 'مدير',

    'common.viewAll': 'عرض الكل',
    'common.save': 'حفظ',
    'common.saving': 'جاري الحفظ',
    'common.cancel': 'إلغاء',
    'common.close': 'إغلاق',
    'common.edit': 'تعديل',
    'common.delete': 'حذف',
    'common.remove': 'إزالة',
    'common.actions': 'إجراءات',
    'common.previous': 'السابق',
    'common.next': 'التالي',
    'common.opensInNewTab': 'يفتح في تبويب جديد',
    'common.pagination': 'تصفح الصفحات',
    'common.totalRows': '{count} في المجموع',
    'common.confirm': 'تأكيد',
    'common.clearFilters': 'مسح الفلاتر',
    'common.back': 'رجوع',
    'common.moreInfo': 'مزيد من المعلومات',
    'common.notFoundHint': 'قد يكون العنوان مكتوبا بشكل خاطئ، أو أن ما كان يشير إليه قد تم حذفه.',
    'common.search': 'بحث',
    'common.noMatches': 'لا توجد نتائج',
    'common.clearDate': 'مسح التاريخ',
    'common.chooseDate': 'اختر تاريخا',

    'export.export': 'تصدير',
    'export.excel': 'Excel',
    'export.pdf': 'PDF',
    'export.working': 'جار التصدير',
    'export.exportedAt': 'تم التصدير في {at}',
    'export.ordersFor': 'طلبات {name}',
    'export.itemsIn': 'منتجات الطلب {number}',
    'export.tooManyRows': 'يتسع ملف PDF لـ {max} صف كحد أقصى. استخدم فلاتر أدق، أو قم بالتصدير إلى Excel بدلا من ذلك.',
    'export.allMatching': 'يتم تنزيل كل الصفوف المطابقة للفلاتر، وليس هذه الصفحة فقط',
    'export.failed': 'تعذر التصدير',
    'export.failedHint': 'لم يتم إنشاء الملف. تحقق من الاتصال وحاول مرة أخرى.',
    'export.nameEn': 'الاسم (بالإنجليزية)',
    'export.nameAr': 'الاسم (بالعربية)',
    'export.phone': 'رقم الهاتف',
    'export.stockAlert': 'تنبيه المخزون',
    'export.starts': 'يبدأ',
    'export.ends': 'ينتهي',
    'export.details': 'التفاصيل',
    'export.actionCode': 'رمز الإجراء',
    'export.yes': 'نعم',
    'export.no': 'لا',

    'lang.arabic': 'العربية',
    'lang.english': 'الإنجليزية',

    'catalog.product': 'المنتج',
    'catalog.newProduct': 'منتج جديد',
    'catalog.editProduct': 'تعديل المنتج',
    'catalog.productNotFound': 'هذا المنتج لم يعد موجودا.',
    'catalog.categoryNotFound': 'هذه الفئة لم تعد موجودة.',
    'catalog.noProducts': 'لا توجد منتجات مطابقة',
    'catalog.noProductsHint': 'جرب إزالة أحد الفلاتر، أو ابحث بكلمة أخرى.',
    'catalog.searchProducts': 'ابحث بالاسم أو رمز المنتج',
    'catalog.allCategories': 'كل الفئات',
    'catalog.allStatuses': 'كل الحالات',
    'catalog.sort': 'الترتيب',
    'catalog.image': 'الصورة',
    'catalog.featured': 'مميز',
    'catalog.lowStock': 'منخفض',
    'catalog.outOfStock': 'نفد المخزون',

    'catalog.basics': 'المعلومات الأساسية',
    'catalog.name': 'الاسم',
    'catalog.slug': 'الرابط',
    'catalog.slugHint': 'العنوان الذي يباع عليه هذا المنتج. تغييره يكسر الروابط الحالية.',
    'catalog.shortDescription': 'وصف مختصر',
    'catalog.shortDescriptionHint': 'سطر واحد يظهر على بطاقات المنتجات.',
    'catalog.description': 'الوصف',

    'catalog.seo': 'محركات البحث',
    'catalog.metaTitle': 'عنوان الميتا',
    'catalog.metaTitleHint': 'يستخدم اسم المنتج عند تركه فارغا.',
    'catalog.metaDescription': 'وصف الميتا',
    'catalog.metaDescriptionHint': 'حوالي ١٥٥ حرفا. يستخدم الوصف المختصر عند تركه فارغا.',

    'catalog.pricing': 'التسعير',
    'catalog.price': 'السعر',
    'catalog.compareAtPrice': 'السعر قبل الخصم',
    'catalog.compareAtHint': 'يظهر مشطوبا بجانب السعر. اتركه فارغا إن لم يكن هناك خصم.',
    'catalog.vatBreakdown': 'السعر شامل الضريبة — {net} قبل الضريبة و{vat} ضريبة.',

    'catalog.stock': 'المخزون',
    'catalog.stockQuantity': 'الكمية المتوفرة',
    'catalog.stockIsPerVariant': 'مجموع مخزون الخيارات. لكل خيار مخزونه في جدول الخيارات.',
    'catalog.lowStockThreshold': 'حد المخزون المنخفض',
    'catalog.lowStockHint': 'يظهر تنبيه في لوحة المعلومات عند وصول المخزون إلى هذا الحد.',

    'catalog.placement': 'التصنيف والعرض',
    'catalog.category': 'الفئة',
    'catalog.chooseCategory': 'اختر فئة',
    'catalog.status': 'الحالة',
    'catalog.sku': 'رمز المنتج',
    'catalog.featureOnHome': 'إبراز في الصفحة الرئيسية',

    'catalog.options': 'الخيارات',
    'catalog.addOption': 'إضافة خيار',
    'catalog.noOptionsHint': 'أضف خيارا — الحجم، درجة الطحن — إذا كان المنتج يباع بأكثر من شكل.',
    'catalog.optionName': 'اسم الخيار',
    'catalog.optionNameAr': 'الحجم',
    'catalog.optionNameEn': 'Size',
    'catalog.optionValue': 'القيمة',
    'catalog.addValue': 'إضافة قيمة',
    'catalog.valueAr': '٢٥٠ جم',
    'catalog.valueEn': '250 g',

    'catalog.variants': 'الأشكال المتاحة',
    'catalog.variant': 'الشكل',
    'catalog.generateVariants': 'توليد الصفوف',
    'catalog.optionsIncomplete': 'أضف قيمة واحدة على الأقل لكل خيار قبل توليد الصفوف.',
    'catalog.variantsOutOfDate':
      'تغيرت الخيارات: {expected} تركيبة مقابل {actual} صفا. أعد التوليد للمطابقة.',
    'catalog.priceDelta': 'فرق السعر',
    'catalog.finalPrice': 'يباع بـ',
    'catalog.active': 'نشط',
    'catalog.hidden': 'مخفي',

    'catalog.images': 'الصور',
    'catalog.uploadImages': 'رفع صور',
    'catalog.noImages': 'لا توجد صور بعد. الصورة الأولى هي التي يعرضها المتجر.',
    'catalog.primaryImage': 'الرئيسية',
    'catalog.moveEarlier': 'تقديم',
    'catalog.moveLater': 'تأخير',
    'catalog.alt': 'الوصف',
    'catalog.editAlt': 'تعديل وصف الصورة',
    'catalog.altAr': 'وصف الصورة',
    'catalog.altEn': 'What the photo shows',
    'catalog.uploadFailed': 'تعذر رفع {name}',

    'catalog.saved': 'تم الحفظ',
    'catalog.slugAdjusted': 'تم حفظ عنوان الصفحة بصيغة «{slug}».',
    'catalog.couldNotSave': 'تعذر الحفظ',
    'catalog.deleted': 'تم الحذف',
    'catalog.couldNotDelete': 'تعذر الحذف',
    'catalog.hasOrders': 'سبق طلب هذا المنتج، لذا يبقى في السجل. استخدم الأرشفة بدلا من ذلك.',
    'catalog.archive': 'أرشفة',
    'catalog.archived': 'تمت الأرشفة',
    'catalog.restore': 'استعادة',
    'catalog.restored': 'تمت الاستعادة',
    'catalog.couldNotArchive': 'تعذرت الأرشفة',
    'catalog.couldNotRestore': 'تعذرت الاستعادة',
    'catalog.deleteProduct': 'حذف المنتج',
    'catalog.deleteHint': 'الحذف نهائي. استخدم الأرشفة إذا سبق طلب هذا المنتج.',
    'catalog.deleteTitle': 'حذف «{name}»؟',
    'catalog.deleteExplain':
      'يحذف هذا المنتج ونسخه وصوره نهائيا. لا يمكن حذف منتج سبق طلبه — استخدم الأرشفة بدلا من ذلك.',
    'catalog.required': 'مطلوب',
    'catalog.bothLanguagesRequired': 'اللغتان مطلوبتان',
    'catalog.pricePositive': 'أدخل سعرا أكبر من صفر',

    'catalog.newCategory': 'فئة جديدة',
    'catalog.editCategory': 'تعديل الفئة',
    'catalog.categoryDialogLede': 'اللغتان مطلوبتان قبل الحفظ.',
    'catalog.noCategories': 'لا توجد فئات بعد.',
    'catalog.productCount': 'المنتجات',
    'catalog.parentCategory': 'تندرج تحت',
    'catalog.topLevel': 'فئة رئيسية',
    'catalog.sortOrder': 'الترتيب',
    'catalog.showInShop': 'إظهار في المتجر',

    'productStatus.Draft': 'مسودة',
    'productStatus.Active': 'نشط',
    'productStatus.Archived': 'مؤرشف',

    'catalogSort.newest': 'الأحدث أولا',
    'catalogSort.name': 'الاسم',
    'catalogSort.price-asc': 'السعر من الأقل للأعلى',
    'catalogSort.price-desc': 'السعر من الأعلى للأقل',
    'catalogSort.stock': 'المخزون من الأقل للأعلى',

    'dashboard.title': 'لوحة المعلومات',
    'dashboard.subtitle': 'حركة البيع وأداء الشهر الحالي',
    'dashboard.trade': 'حركة البيع',
    'dashboard.needsAttention': 'بحاجة إلى متابعة',
    'dashboard.revenueThisMonth': 'إيرادات هذا الشهر',
    'dashboard.ordersThisMonth': 'طلبات هذا الشهر',
    'dashboard.revenueToday': 'إيرادات اليوم',
    'dashboard.products': 'المنتجات',
    'dashboard.customers': 'العملاء',
    'dashboard.pendingPayment': 'بانتظار الدفع',
    'dashboard.awaitingFulfilment': 'للتجهيز',
    'dashboard.lowStock': 'كمية منخفضة',
    'dashboard.pendingReviews': 'تقييمات للمراجعة',
    'dashboard.newInquiries': 'استفسارات جديدة',
    'dashboard.shippedToday': 'شحنات اليوم',
    'dashboard.refundsDue': 'استردادات مستحقة',
    'dashboard.recentOrders': 'أحدث الطلبات',
    'dashboard.noOrdersYet': 'لا توجد طلبات بعد.',

    'orders.number': 'الطلب',
    'orders.customer': 'العميل',
    'orders.placed': 'تاريخ الطلب',
    'orders.status': 'الحالة',
    'orders.total': 'الإجمالي',
    'orders.order': 'الطلب',
    'orders.search': 'ابحث برقم الطلب أو البريد أو الاسم',
    'orders.allStatuses': 'كل الحالات',
    'orders.from': 'من',
    'orders.to': 'إلى',
    'orders.none': 'لا توجد طلبات مطابقة',
    'orders.noneHint': 'جرب إزالة أحد الفلاتر، أو وسع المدى الزمني.',
    'orders.items': 'المنتجات',
    'orders.item': 'المنتج',
    'orders.unitPrice': 'سعر الوحدة',
    'orders.quantity': 'الكمية',
    'orders.lineTotal': 'إجمالي السطر',
    'orders.subtotal': 'المجموع الفرعي',
    'orders.discount': 'الخصم',
    'orders.shipping': 'الشحن',
    'orders.freeShipping': 'مجاني',
    'orders.vatIncluded': 'منها ضريبة',
    'orders.timeline': 'السجل',
    'orders.bySystem': 'المتجر',
    'orders.shipTo': 'التوصيل إلى',
    'orders.payment': 'الدفع',
    'orders.paid': 'مدفوع',
    'orders.notPaid': 'لم يدفع بعد',
    'orders.noReference': 'لا يوجد مرجع دفع بعد.',
    'orders.notFound': 'هذا الطلب غير موجود.',
    'orders.placedAt': 'تم الطلب في {at}',
    'orders.paidAt': 'تم الدفع في {at}',
    'orders.cancelledAt': 'تم الإلغاء في {at}',
    'orders.finished': 'لا يوجد إجراء آخر.',
    'orders.courier': 'شركة الشحن',
    'orders.trackingNumber': 'رقم التتبع',
    'orderNote.placed': 'تم إنشاء الطلب.',
    'orderNote.paid': 'تم استلام الدفع.',
    'orderNote.declined': 'تم رفض الدفع.',
    'orderNote.expired': 'تم الإلغاء: لم يتم الدفع في الوقت المحدد.',
    'orderNote.cancelledByCustomer': 'ألغاه العميل.',
    'orderNote.cancelledByCustomerBecause': 'ألغاه العميل: {reason}',
    'coupons.new': 'رمز جديد',
    'coupons.edit': 'تعديل الرمز',
    'coupons.editorLede': 'قيمة الخصم، وشروطه على السلة، ومتى يمكن استخدامه.',
    'coupons.none': 'لا توجد رموز خصم بعد.',
    'coupons.notFound': 'رمز الخصم غير موجود.',
    'coupons.code': 'الرمز',
    'coupons.codeHint': 'ما يكتبه المتسوق. أحرف وأرقام وشرطات؛ لا يميز بين الأحرف الكبيرة والصغيرة.',
    'coupons.discount': 'الخصم',
    'coupons.type': 'النوع',
    'coupons.typePercentage': 'نسبة مئوية',
    'coupons.typeFixed': 'مبلغ ثابت',
    'coupons.percentOff': 'نسبة الخصم',
    'coupons.amountOff': 'مبلغ الخصم (درهم)',
    'coupons.minimum': 'الحد الأدنى للسلة (درهم)',
    'coupons.minimumHint': 'اختياري. لا يعمل الرمز إلا عند بلوغ السلة هذا المبلغ.',
    'coupons.cap': 'أقصى خصم (درهم)',
    'coupons.capHint': 'اختياري. يمنع النسبة من خصم مبلغ كبير من سلة كبيرة.',
    'coupons.limits': 'متى وكم مرة',
    'coupons.usageLimit': 'عدد مرات الاستخدام',
    'coupons.usageLimitHint': 'اختياري. لجميع المتسوقين. اتركه فارغا لعدم التقييد.',
    'coupons.switchedOn': 'مفعل',
    'coupons.switchedOnHint': 'أوقفه لإيقاف عمل الرمز دون حذفه.',
    'coupons.window': 'الصلاحية',
    'coupons.used': 'الاستخدام',
    'coupons.usedOf': '{used} من {limit}',
    'coupons.minimumShort': 'سلة أكثر من {amount}',
    'coupons.capShort': 'حتى {amount}',
    'coupons.fromDate': 'من {date}',
    'coupons.untilDate': 'حتى {date}',
    'coupons.noEnd': 'بلا تاريخ انتهاء',
    'coupons.live': 'فعال',
    'coupons.off': 'متوقف',
    'coupons.expired': 'منتهي',
    'coupons.usedUp': 'مستنفد',
    'coupons.scheduled': 'مجدول',
    'coupons.saved': 'تم حفظ رمز الخصم',
    'coupons.deleted': 'تم حذف رمز الخصم',
    'coupons.delete': 'حذف الرمز',
    'coupons.deleteTitle': 'حذف «{code}»؟',
    'coupons.deleteHint': 'لا يمكن حذف إلا رمز لم يستخدمه أحد. بعد الاستخدام، أوقفه بدلا من ذلك.',
    'coupons.inUse': 'تم استخدام هذا الرمز في طلبات، لذا يبقى في السجل. أوقفه بدلا من ذلك.',
    'coupons.codeInvalid': 'أدخل رمزا من أحرف أو أرقام أو شرطات.',
    'coupons.codeTaken': 'هذا الرمز مستخدم لرمز خصم آخر.',
    'coupons.valueInvalid': 'أدخل خصما أكبر من صفر.',
    'coupons.percentTooHigh': 'لا يمكن أن تزيد النسبة على 100.',
    'coupons.endsBeforeStart': 'يجب أن ينتهي الرمز بعد بدايته.',
    'orders.refundDue': 'استرداد مستحق',
    'orders.refundDueAmount': 'استرداد مستحق: {amount}',
    'orders.refundHow': 'أعد المبلغ عبر البنك أو بوابة الدفع، ثم سجل ذلك هنا.',
    'orders.recordRefund': 'تسجيل الاسترداد',
    'orders.recordRefundTitle': 'تسجيل استرداد {amount}',
    'orders.refundReference': 'مرجع البنك أو البوابة',
    'orders.refundReferenceHint': 'اختياري. يستخدم عند مطابقة الحسابات.',
    'orders.markRefunded': 'تم الاسترداد',
    'orders.refundRecorded': 'تم تسجيل الاسترداد',
    'orders.couldNotRecordRefund': 'تعذر تسجيل الاسترداد',
    'orders.refundedAt': 'تم استرداد المبلغ {at}',
    'orders.cancelPaidHint':
      'هذا الطلب مدفوع. إلغاؤه يجعل استرداد {amount} مستحقا. أعد المبلغ عبر البنك أو بوابة الدفع، ثم سجل ذلك في هذه الصفحة.',
    'orders.nextStepsFailed': 'تعذر تحميل الخطوات التالية.',
    'orders.note': 'ملاحظة للعميل',
    'orders.noteHint': 'اختيارية. تظهر في صفحة طلب العميل وترسل ضمن البريد الذي يصله.',
    'orders.noteHintNoEmail': 'اختيارية. تظهر في صفحة طلب العميل. لا ترسل هذه الخطوة بريدا.',
    'orders.noteHintNoAddress': 'اختيارية. تظهر في صفحة تتبع الطلب. لا يوجد بريد إلكتروني لهذا الطلب.',
    'orders.confirmMove': 'النقل إلى «{status}»؟',
    'orders.moved': 'تم النقل إلى «{status}»',
    'orders.couldNotMove': 'تعذر نقل هذا الطلب',

    'orderAction.Paid': 'تحديد كمدفوع',
    'orderAction.Processing': 'بدء التجهيز',
    'orderAction.Shipped': 'تحديد كمشحون',
    'orderAction.Delivered': 'تأكيد التسليم',
    'orderAction.Cancelled': 'إلغاء الطلب',
    'orderAction.PaymentFailed': 'تحديد كفشل دفع',

    'customers.customer': 'العميل',
    'customers.search': 'ابحث بالاسم أو البريد أو الهاتف',
    'customers.account': 'الحساب',
    'customers.allAccounts': 'كل الحسابات',
    'customers.activeOnly': 'النشطة فقط',
    'customers.blockedOnly': 'المحظورة فقط',
    'customers.sort': 'الترتيب',
    'customers.hasOrdered': 'لديه طلبات',
    'customers.none': 'لا يوجد عملاء مطابقون',
    'customers.noneHint': 'جرب إزالة أحد الفلاتر، أو ابحث بالبريد الإلكتروني.',
    'customers.registered': 'تاريخ التسجيل',
    'customers.lastOrder': 'آخر طلب',
    'customers.orders': 'الطلبات',
    'customers.latestOrders': 'أحدث {shown} من {total}.',
    'customers.allOrders': 'عرض كل طلباته',
    'customers.spent': 'إجمالي الإنفاق',
    'customers.spend': 'الإنفاق',
    'customers.neverOrdered': 'لم يطلب بعد.',
    'customers.blocked': 'محظور',
    'customers.active': 'نشط',
    'customers.unconfirmed': 'البريد غير مؤكد',
    'customers.notFound': 'هذا العميل غير موجود.',
    'customers.registeredAt': 'تم التسجيل في {at}',
    'customers.blockedAt': 'تم الحظر في {at}',
    'customers.contact': 'بيانات التواصل',
    'customers.writesIn': 'ترسل إليه الرسائل بـ{language}.',
    'customers.addresses': 'العناوين',
    'customers.noAddresses': 'لا توجد عناوين محفوظة.',
    'customers.defaultAddress': 'الافتراضي',
    'customers.reviews': 'التقييمات',
    'customers.noReviews': 'لم يكتب أي تقييم.',
    'customers.rating': '{rating} من ٥',
    'customers.block': 'حظر',
    'customers.unblock': 'رفع الحظر',
    'customers.resendConfirmation': 'إرسال رسالة التأكيد',
    'customers.confirmationSent': 'تم إرسال رسالة التأكيد إلى {email}',
    'customers.couldNotSendConfirmation': 'تعذر إرسال رسالة التأكيد',
    'customers.blockTitle': 'حظر هذا الحساب؟',
    'customers.blockExplain':
      'لن يتمكن من تسجيل الدخول. تبقى طلباته وتقييماته وعناوينه كما هي، ويمكنك التراجع في أي وقت.',
    'customers.reason': 'السبب',
    'customers.reasonHint': 'داخلي. يحفظ في سجل التدقيق ولا يظهر للعميل أبدا.',
    'customers.blockedNow': 'تم حظر الحساب',
    'customers.unblocked': 'تم رفع الحظر',
    'customers.couldNotChange': 'تعذر تغيير هذا الحساب',
    'customers.deleteTitle': 'حذف هذا الحساب؟',
    'customers.deleteExplain':
      'الحذف نهائي. ممكن فقط لأن هذا العميل لم يطلب أي شيء — استخدم الحظر إن كنت تريد منعه من الدخول فقط.',
    'customers.deleted': 'تم حذف الحساب',
    'customers.couldNotDelete': 'تعذر حذف هذا الحساب',

    'customerSort.newest': 'الأحدث أولا',
    'customerSort.name': 'الاسم',
    'customerSort.spent': 'الأكثر إنفاقا',
    'customerSort.orders': 'الأكثر طلبا',

    'reviewStatus.Pending': 'بانتظار المراجعة',
    'reviewStatus.Approved': 'منشور',
    'reviewStatus.Hidden': 'مرفوض',

    'reviews.all': 'الكل',
    'reviews.filterByStatus': 'تصفية حسب الحالة',
    'reviews.none': 'لا يوجد شيء هنا',
    'reviews.inboxClear': 'لا شيء ينتظر المراجعة',
    'reviews.inboxClearHint': 'التقييمات الجديدة تصل هنا للاعتماد قبل ظهورها في المتجر.',
    'reviews.verified': 'شراء موثق',
    'reviews.publish': 'نشر',
    'reviews.reject': 'رفض',
    'reviews.published': 'تم نشر التقييم',
    'reviews.rejected': 'تم رفض التقييم',
    'reviews.couldNotModerate': 'تعذرت مراجعة هذا التقييم',
    'reviews.reply': 'رد',
    'reviews.editReply': 'تعديل الرد',
    'reviews.replyTitle': 'الرد على هذا التقييم',
    'reviews.replyHint': 'ينشر أسفل التقييم في المتجر باسم أوج.',
    'reviews.replied': 'تم حفظ الرد',
    'reviews.couldNotReply': 'تعذر حفظ الرد',
    'reviews.shopReplied': 'ردت أوج',

    'inquiries.search': 'ابحث بالاسم أو البريد أو الشركة أو الموضوع',
    'inquiries.status': 'الحالة',
    'inquiries.type': 'النوع',
    'inquiries.allStatuses': 'كل الحالات',
    'inquiries.allTypes': 'كل الأنواع',
    'inquiries.none': 'لا توجد استفسارات مطابقة لهذه الفلاتر.',
    'inquiries.pickOne': 'لا شيء مفتوح',
    'inquiries.pickOneHint': 'اختر استفسارا من القائمة لقراءته والرد عليه.',
    'inquiries.notFound': 'هذا الاستفسار لم يعد موجودا.',
    'inquiries.noteCount': 'الملاحظات: {count}',
    'inquiries.pickedUpBy': 'بمتابعة {who}',
    'inquiries.company': 'الشركة',
    'inquiries.quantity': 'الكمية التقديرية',
    'inquiries.frequency': 'التكرار',
    'inquiries.notes': 'ملاحظات داخلية',
    'inquiries.noNotes': 'لا توجد ملاحظات بعد.',
    'inquiries.addNote': 'إضافة ملاحظة',
    'inquiries.someone': 'أحد الزملاء',
    'inquiries.emailBack': 'الرد بالبريد',
    'inquiries.movedTo': 'تم النقل إلى «{status}»',
    'inquiries.couldNotMove': 'تعذر نقل هذا الاستفسار',
    'inquiries.couldNotAddNote': 'تعذرت إضافة الملاحظة',

    'inquiryStatus.New': 'جديد',
    'inquiryStatus.InProgress': 'قيد المتابعة',
    'inquiryStatus.Resolved': 'تم الحل',
    'inquiryStatus.Spam': 'مزعج',

    'inquiryAction.New': 'إعادة فتح',
    'inquiryAction.InProgress': 'بدء المتابعة',
    'inquiryAction.Resolved': 'إغلاق',
    'inquiryAction.Spam': 'تحديد كمزعج',

    'inquiryType.Contact': 'تواصل',
    'inquiryType.Business': 'استفسار تجاري',

    'content.sections': 'أقسام المحتوى',
    'content.announcements': 'الإعلانات',
    'content.faq': 'الأسئلة الشائعة',
    'content.media': 'الصور',
    'content.saved': 'تم الحفظ',
    'content.couldNotSave': 'تعذر الحفظ',
    'content.deleted': 'تم الحذف',
    'content.couldNotDelete': 'تعذر الحذف',
    'content.live': 'ظاهر في الموقع',
    'content.hidden': 'مخفي',
    'content.showOnSite': 'إظهار في الموقع',


    'content.announcementsLede': 'الشريط أعلى المتجر.',
    'content.announcementNotFound': 'هذا الإعلان لم يعد موجودا.',
    'content.newAnnouncement': 'إعلان جديد',
    'content.editAnnouncement': 'تعديل الإعلان',
    'content.noAnnouncements': 'لا توجد إعلانات بعد',
    'content.noAnnouncementsHint': 'الإعلان شريط يظهر أعلى المتجر. أضف واحدا وحدد مدة ظهوره.',
    'content.message': 'النص',
    'content.linkUrl': 'الرابط',
    'content.linkLabel': 'نص الرابط',
    'content.schedule': 'متى يظهر',
    'content.startsAt': 'يبدأ',
    'content.endsAt': 'ينتهي',
    'content.startsAtHint': 'اتركه فارغا ليبدأ فورا.',
    'content.timeOfDay': 'الوقت',
    'content.switchedOn': 'مفعل',
    'content.showingNow': 'ظاهر الآن',
    'content.scheduled': 'مجدول',
    'content.switchedOff': 'معطل',
    'content.from': 'من {at}',
    'content.until': 'حتى {at}',

    'content.faqLede': 'الأسئلة التي يجيب عنها المتجر، باللغتين.',
    'content.newQuestion': 'سؤال جديد',
    'content.noFaq': 'لا توجد أسئلة بعد',
    'content.noFaqHint': 'الأسئلة المضافة هنا تظهر في المتجر بالترتيب الذي تحدده.',
    'content.question': 'السؤال',
    'content.answer': 'الإجابة',
    'content.answerRequired': 'الإجابة مطلوبة باللغتين',
    'content.category': 'المجموعة',
    'content.categoryHint': 'اختيارية. تعرض الأسئلة ذات المجموعة نفسها معا.',

    'content.mediaLede': 'الصور المستخدمة في المتجر. تختار منها الصفحات.',
    'content.uploadMedia': 'رفع',
    'content.noMedia': 'لم يرفع شيء بعد',
    'content.noMediaHint': 'ارفع صورة هنا، أو من محرر الصفحة التي تحتاجها.',
    'content.noAlt': 'لا يوجد وصف للصورة — لن يقرأ قارئ الشاشة شيئا.',


    'content.formatting': 'التنسيق',
    'content.bold': 'عريض',
    'content.italic': 'مائل',
    'content.bulletList': 'قائمة نقطية',
    'content.numberedList': 'قائمة مرقمة',
    'content.link': 'رابط',
    'content.linkPrompt': 'إلى أين يشير هذا الرابط؟',
    'content.pages': 'الصفحات',
    'content.groupShop': 'صفحات المتجر',
    'content.groupPolicies': 'السياسات',
    'content.groupOther': 'صفحات أخرى',
    'content.pageHome': 'الصفحة الرئيسية',
    'content.pageHomeSummary': 'الواجهة في الأعلى وعنوان المنتجات المميزة.',
    'content.pageStory': 'قصتنا',
    'content.pageStorySummary': 'صفحة القصة: عنوانها وصورتها ونص القصة.',
    'content.pageMenu': 'القائمة',
    'content.pageMenuSummary': 'النص أعلى قائمة المقهى.',
    'content.pageBusiness': 'للأعمال',
    'content.pageBusinessSummary': 'صفحة الأعمال: مقدمتها وأي أقسام إضافية.',
    'content.pageContact': 'تواصل معنا',
    'content.pageContactSummary': 'العنوان والسطر أعلى صفحة التواصل.',
    'content.pagePrivacy': 'سياسة الخصوصية',
    'content.pageTerms': 'الشروط والأحكام',
    'content.pagePolicySummary': 'العنوان وملخص قصير والأقسام المرقمة.',
    'content.pageOtherSummary': 'نصوص محفوظة تحت اسم هذه الصفحة، وقد لا يعرضها المتجر في أي مكان.',
    'content.pageNotFound': 'هذه الصفحة غير معروفة في المحتوى.',
    'content.partHero': 'الواجهة',
    'content.partHeroHint': 'أول ما يراه الزائر في الصفحة الرئيسية.',
    'content.partFeatured': 'المنتجات المميزة',
    'content.partFeaturedHint': 'العنوان فوق المنتجات المختارة للصفحة الرئيسية.',
    'content.partStory': 'القصة',
    'content.partStoryHint': 'تظهر في صفحة القصة وبجانبها صورتها.',
    'content.partHeading': 'عنوان الصفحة',
    'content.partMenuHint': 'أعلى القائمة. اتركه فارغا لاستخدام نص المتجر الافتراضي.',
    'content.partBusinessHint': 'أعلى صفحة الأعمال، فوق نموذج الاستفسار.',
    'content.partContactHint': 'أعلى صفحة التواصل.',
    'content.partPolicyHint': 'العنوان وسطر تحته والملخص الذي يقرؤه الزائر أولا.',
    'content.fieldTitle': 'العنوان',
    'content.fieldSubtitle': 'سطر تحت العنوان',
    'content.fieldBody': 'النص',
    'content.fieldSummary': 'الملخص',
    'content.fieldButton': 'نص الزر',
    'content.fieldButtonLink': 'رابط الزر',
    'content.fieldButtonLinkHint': 'صفحة من المتجر مثل ‎/shop أو عنوان ويب كامل. هو نفسه في اللغتين.',
    'content.fieldImage': 'الصورة',
    'content.showOnSiteHint': 'عند الإيقاف يبقى هنا ولا يعرضه المتجر.',
    'content.writingIn': 'لغة الكتابة',
    'content.gaps': 'ناقص {count}',
    'content.otherSays': 'لم يكتب بعد. النص ب{language}:',
    'content.viewOnSite': 'عرض في المتجر',
    'content.sectionsTitle': 'الأقسام',
    'content.sectionsHint': 'تظهر بهذا الترتيب مرقمة. لا يتغير شيء في المتجر حتى تحفظ الصفحة.',
    'content.addSection': 'إضافة قسم',
    'content.untitledSection': 'قسم بدون عنوان',
    'content.moveUp': 'نقل «{name}» للأعلى',
    'content.moveDown': 'نقل «{name}» للأسفل',
    'content.moveUpShort': 'للأعلى',
    'content.moveDownShort': 'للأسفل',
    'content.removeSection': 'حذف «{name}»',
    'content.savePage': 'حفظ الصفحة',
    'content.unsaved': 'تغييرات غير محفوظة',
    'content.leaveTitle': 'المغادرة دون حفظ؟',
    'content.leaveHint': 'ستضيع التغييرات التي أجريتها على هذه الصفحة.',
    'editor.leaveHint': 'ستضيع التغييرات التي لم تحفظها.',
    'audit.auth.login.succeeded': 'تسجيل دخول',
    'audit.auth.login.failed': 'فشل تسجيل الدخول',
    'audit.auth.login.blocked': 'محاولة دخول لحساب محظور',
    'audit.auth.lockout': 'قفل الحساب بعد محاولات فاشلة',
    'audit.auth.registered': 'إنشاء حساب',
    'audit.auth.email.confirmed': 'تأكيد البريد الإلكتروني',
    'audit.auth.email.confirmation_resent': 'إعادة إرسال رسالة التأكيد',
    'audit.auth.signed_out': 'تسجيل خروج',
    'audit.auth.token.refreshed': 'تجديد الجلسة',
    'audit.auth.token.reuse_detected': 'اشتباه بسرقة الجلسة',
    'audit.auth.password.reset_requested': 'طلب إعادة تعيين كلمة المرور',
    'audit.auth.password.reset': 'إعادة تعيين كلمة المرور',
    'audit.auth.password.changed': 'تغيير كلمة المرور',
    'audit.auth.profile.updated': 'تحديث الملف الشخصي',
    'audit.auth.external.linked': 'ربط حساب Google',
    'audit.customer.blocked': 'حظر عميل',
    'audit.customer.unblocked': 'رفع الحظر عن عميل',
    'audit.customer.deleted': 'حذف عميل',
    'audit.staff.invited': 'دعوة موظف',
    'audit.staff.role_changed': 'تغيير دور موظف',
    'audit.staff.blocked': 'حظر موظف',
    'audit.staff.unblocked': 'رفع الحظر عن موظف',
    'audit.catalog.category.created': 'إنشاء فئة',
    'audit.catalog.category.updated': 'تحديث فئة',
    'audit.catalog.category.deleted': 'حذف فئة',
    'audit.catalog.product.created': 'إنشاء منتج',
    'audit.catalog.product.updated': 'تحديث منتج',
    'audit.catalog.product.status_changed': 'تغيير حالة منتج',
    'audit.catalog.product.image_added': 'إضافة صورة منتج',
    'audit.catalog.product.deleted': 'حذف منتج',
    'audit.content.block.created': 'إضافة محتوى',
    'audit.content.block.updated': 'تحديث محتوى',
    'audit.content.media.deleted': 'حذف ملف وسائط',
    'audit.coupon.created': 'إنشاء رمز خصم',
    'audit.coupon.updated': 'تحديث رمز خصم',
    'audit.coupon.deleted': 'حذف رمز خصم',
    'audit.inquiry.received': 'استلام استفسار',
    'audit.inquiry.status_changed': 'تغيير حالة استفسار',
    'audit.order.placed': 'إنشاء طلب',
    'audit.order.paid': 'دفع طلب',
    'audit.order.cancelled': 'إلغاء طلب',
    'audit.order.status_changed': 'تغيير حالة طلب',
    'audit.order.refunded': 'تسجيل استرداد',
    'audit.review.submitted': 'إرسال تقييم',
    'audit.review.moderated': 'مراجعة تقييم',
    'audit.review.replied': 'الرد على تقييم',
    'audit.settings.updated': 'تغيير الإعدادات',
    'confirm.deleteNamed': 'حذف «{name}»؟',
    'confirm.deleteQuestion': 'حذف «{name}»؟',
    'confirm.deleteAnnouncement': 'حذف هذا الإعلان؟',
    'confirm.deleteImage': 'حذف هذه الصورة؟',
    'confirm.deleteImageBody': 'تزال من المنتج ويحذف ملفها. لا يمكن التراجع عن ذلك.',
    'confirm.deleteMediaBody': 'أي مكان في المتجر يستخدمها سيظهر فارغا. لا يمكن التراجع عن ذلك.',
    'confirm.cannotUndo': 'لا يمكن التراجع عن ذلك.',
    'content.keepEditing': 'متابعة التحرير',
    'content.discard': 'تجاهل التغييرات',
    'content.sectionCount': 'الأقسام: {count}',
    'content.hiddenCount': 'المخفي: {count}',
    'content.missingArabic': 'العربية ناقصة في {count}',
    'content.missingEnglish': 'الإنجليزية ناقصة في {count}',
    'content.bothLanguages': 'اللغتان مكتملتان',
    'content.featuredNow': 'المنتجات المميزة الآن: {count}',
    'content.chooseFeatured': 'اخترها من المنتجات',
    'content.chooseImage': 'اختيار صورة',
    'content.chooseImageHint': 'من مكتبة الصور، أو ارفع صورة جديدة.',
    'content.changeImage': 'تغيير الصورة',
    'content.libraryLede': 'اختر صورة، أو ارفع واحدة من جهازك.',
    'content.uploadNew': 'رفع صورة جديدة',
    'content.useImage': 'استخدام هذه الصورة',
    'content.bodyPlaceholder': 'ابدأ الكتابة…',
    'content.textStyle': 'نمط النص',
    'content.styleParagraph': 'فقرة',
    'content.styleHeading': 'عنوان',
    'content.styleSubheading': 'عنوان فرعي',
    'content.underline': 'تسطير',
    'content.quote': 'اقتباس',
    'content.undo': 'تراجع',
    'content.redo': 'إعادة',
    'content.linkApply': 'تطبيق',
    'content.linkRemove': 'إزالة الرابط',
    'content.editQuestion': 'تعديل السؤال',
    'content.questionNotFound': 'هذا السؤال لم يعد موجودا.',
    'content.scheduleHint': 'اتركهما فارغين ليظهر حتى يتم إيقافه.',
    'content.switchedOnHint': 'عند الإيقاف لا يظهر مهما كانت التواريخ.',

    'settings.shop': 'المتجر',
    'settings.staff': 'الموظفون',
    'settings.audit': 'سجل التدقيق',
    'settings.saved': 'تم الحفظ',
    'settings.couldNotSave': 'تعذر الحفظ',
    'settings.noChanges': 'لا تغييرات',
    'settings.superAdminOnly': 'الإعدادات متاحة للمدير العام فقط.',
    'settings.secretSet': 'محفوظ. اتركه كما هو للإبقاء عليه، أو اكتب قيمة جديدة.',
    'settings.secretUnset': 'غير محدد.',

    'settingGroup.Store': 'بيانات المتجر',
    'settingGroup.Localisation': 'اللغة والعملة',
    'settingGroup.Shipping': 'التوصيل',
    'settingGroup.Checkout': 'إتمام الطلب',
    'settingGroup.Email': 'الإشعارات',
    'settingGroup.Reviews': 'التقييمات',
    'settingGroup.Maintenance': 'وضع الصيانة',

    'settingKey.Store.NameEn': 'اسم المتجر (بالإنجليزية)',
    'settingKey.Store.NameAr': 'اسم المتجر (بالعربية)',
    'settingKey.Store.SupportEmail': 'بريد الدعم',
    'settingKey.Store.SupportPhone': 'رقم الجوال',
    'settingKey.Store.Landline': 'الهاتف الأرضي',
    'settingKey.Store.AddressEn': 'العنوان (بالإنجليزية)',
    'settingKey.Store.AddressAr': 'العنوان (بالعربية)',
    'settingKey.Store.StorefrontBaseUrl': 'عنوان المتجر الإلكتروني',
    'settingKey.Store.AdminBaseUrl': 'عنوان لوحة الإدارة',

    'settingKey.Localisation.DefaultLocale': 'اللغة الافتراضية',
    'settingKey.Localisation.Currency': 'العملة',
    'settingKey.Localisation.VatRate': 'نسبة ضريبة القيمة المضافة',

    'settingKey.Shipping.ShippingFee': 'رسوم التوصيل',
    'settingKey.Shipping.FreeShippingThreshold': 'التوصيل مجانا ابتداء من',
    'settingKey.Shipping.ServedEmirates': 'الإمارات المشمولة بالتوصيل',

    'settingKey.Checkout.GuestCheckoutEnabled': 'السماح بالشراء دون حساب',
    'settingKey.Checkout.MinimumOrderAmount': 'الحد الأدنى للطلب',
    'settingKey.Checkout.OrderNumberPrefix': 'بادئة رقم الطلب',
    'settingKey.Checkout.AutoCancelUnpaidAfterHours': 'إلغاء الطلبات غير المدفوعة بعد (بالساعات)',

    'settingKey.Email.AdminRecipients': 'من يستلم إشعارات الموظفين',
    'settings.recipientsFallback': 'فارغ، لذا ترسل إشعارات الموظفين إلى بريد الدعم في بيانات المتجر.',
    'settings.checkMarked': 'لم يحفظ شيء. راجع الحقل المحدد.',
    'settings.invalidValue': 'قيمة غير صالحة. المتوقع: {expected}',
    'settingKey.Email.NotifyOrderConfirmation': 'إشعار العميل باستلام طلبه',
    'settingKey.Email.NotifyOrderShipped': 'إشعار العميل بشحن طلبه',
    'settingKey.Email.NotifyAdminNewOrder': 'إشعار الموظفين بطلب جديد',
    'settingKey.Email.NotifyAdminNewInquiry': 'إشعار الموظفين باستفسار جديد',
    'settingKey.Email.NotifyAdminLowStock': 'إشعار الموظفين بانخفاض المخزون',

    'settingKey.Reviews.AutoApprove': 'نشر التقييمات دون مراجعتها',
    'settingKey.Reviews.MinimumCharacters': 'أقصر تقييم مسموح (بالأحرف)',
    'settingKey.Reviews.RequireVerifiedPurchase': 'التقييم لمن اشترى المنتج فقط',

    'settingKey.Maintenance.Enabled': 'إيقاف المتجر مؤقتا',
    'settingKey.Maintenance.MessageEn': 'رسالة الإيقاف (بالإنجليزية)',
    'settingKey.Maintenance.MessageAr': 'رسالة الإيقاف (بالعربية)',

    'settingKind.Text': ' ',
    'settingKind.MultilineText': ' ',
    'settingKind.Number': 'رقم صحيح.',
    'settingKind.Money': 'مبلغ بالدرهم، مثل 25.00',
    'settingKind.Percentage': 'نسبة بين 0 و1، مثل 0.05 لـ5%.',
    'settingKind.Boolean': ' ',
    'settingKind.Email': 'بريد إلكتروني.',
    'settingKind.Url': 'عنوان كامل يبدأ بـhttp أو https.',
    'settingKind.Locale': '«ar» أو «en».',
    'settingKind.EmirateList': 'الإمارات مفصولة بفواصل. الفراغ يعني جميعها.',
    'settingKind.EmailList': 'عناوين بريد مفصولة بفواصل.',

    'settings.staffLede': 'من يمكنه الدخول إلى لوحة التحكم.',
    'settings.invite': 'دعوة',
    'settings.inviteTitle': 'دعوة زميل',
    'settings.inviteExplain':
      'تصله رسالة برابط لتعيين كلمة المرور بنفسه. لا يتم اختيار كلمة مرور له.',
    'settings.inviteIncomplete': 'الاسم والبريد الإلكتروني مطلوبان',
    'settings.invited': 'تم إرسال الدعوة',
    'settings.couldNotInvite': 'تعذر إرسال الدعوة',
    'settings.confirm.promoteTitle': 'ترقية {name} إلى مدير عام؟',
    'settings.confirm.promoteBody':
      'يستطيع المدير العام تغيير إعدادات المتجر وإدارة الموظفين وحذف المنتجات والعملاء نهائيا.',
    'settings.confirm.demoteTitle': 'جعل {name} مديرا؟',
    'settings.confirm.demoteBody':
      'يحتفظ بالعمل اليومي لكنه يفقد إعدادات المتجر وإدارة الموظفين والحذف النهائي.',
    'settings.confirm.blockTitle': 'حظر {name}؟',
    'settings.confirm.blockBody':
      'يتم تسجيل خروجه ولا يستطيع الدخول إلى لوحة الإدارة حتى يتم رفع الحظر.',
    'settings.inviteExists':
      'لهذا البريد حساب بالفعل، ربما كعميل. يحتاج الموظفون إلى بريد خاص بهم؛ استخدم بريد العمل.',
    'settings.person': 'الشخص',
    'settings.role': 'الدور',
    'settings.roleHint': 'المدير العام يمكنه أيضا فتح الإعدادات وإدارة الموظفين وحذف السجلات.',
    'settings.added': 'تمت الإضافة',
    'settings.lastSignIn': 'آخر دخول',
    'settings.neverSignedIn': 'لم يدخل بعد',
    'settings.thisIsYou': 'هذا حسابك',
    'settings.promote': 'ترقية إلى مدير عام',
    'settings.demote': 'تحويل إلى مدير',
    'settings.roleChanged': 'تم تغيير الدور',
    'settings.couldNotChangeRole': 'تعذر تغيير الدور',
    'settings.firstName': 'الاسم الأول',
    'settings.lastName': 'اسم العائلة',

    'settings.auditActor': 'ابحث حسب من قام بالإجراء',
    'settings.auditArea': 'المجال',
    'settings.auditAllAreas': 'كل شيء',
    'settings.auditNone': 'لا توجد سجلات مطابقة لهذه الفلاتر.',
    'settings.auditWhen': 'الوقت',
    'settings.auditAction': 'ما حدث',
    'settings.auditWho': 'من',
    'settings.auditWhat': 'على',
    'settings.auditFrom': 'من عنوان',

    'auditArea.auth': 'تسجيلات الدخول',
    'auditArea.customer': 'العملاء',
    'auditArea.staff': 'الموظفون',
    'auditArea.order': 'الطلبات',
    'auditArea.catalog': 'الكتالوج',
    'auditArea.review': 'التقييمات',
    'auditArea.inquiry': 'الاستفسارات',
    'auditArea.content': 'المحتوى',
    'auditArea.setting': 'الإعدادات',

    'orderStatus.PendingPayment': 'بانتظار الدفع',
    'orderStatus.Paid': 'مدفوع',
    'orderStatus.PaymentFailed': 'فشل الدفع',
    'orderStatus.Processing': 'قيد التجهيز',
    'orderStatus.Shipped': 'في الطريق',
    'orderStatus.Delivered': 'تم التسليم',
    'orderStatus.Cancelled': 'ملغي',


    'a11y.notifications': 'الإشعارات',
    'a11y.scrollableTable': 'جدول قابل للتمرير',
    'a11y.dismissNotification': 'إغلاق الإشعار',
    'a11y.skipToContent': 'تخط إلى المحتوى',
  },
} as const;

export type MessageKey = keyof (typeof messages)['en'];
